import { Box, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import { FieldType, KeyValuePairStringIntFieldValue } from '.';
import { InputValidationResult } from '..';
import {
  FieldTypeEnumResponse,
  FieldWithKeyValuePairStringIntResponse,
  ValidationTypeEnumResponse,
} from '../../.generated/admin-api';
import { BooleanField } from './fieldTypes/BooleanField';
import { ImageField } from './fieldTypes/ImageField';
import { NumberField } from './fieldTypes/NumberField';
import { KeyValuePairStringIntField } from './fieldTypes/KeyValuePairStringIntField';
import { OptionsField } from './fieldTypes/OptionsField';
import { StackNumberField } from './fieldTypes/StackNumberField';
import { TextField } from './fieldTypes/TextField';
import { validateField } from './fieldValidation';
import { stackNumberFieldId } from './RegistrationFlow';
import { MultiOptionsField } from './fieldTypes/MultiOptionsField';

type RegistrationFlowFieldInputProps = {
  field: FieldType;
  defaultValue?: unknown;
  disabled?: boolean;
  onValueChangeOrValidated?: (
    fieldId: string,
    value: unknown,
    isValid: boolean,
  ) => void;
};

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
}));

const RegistrationFlowField: React.FC<RegistrationFlowFieldInputProps> = (
  props,
) => {
  const { field, defaultValue, onValueChangeOrValidated, disabled } = props;

  const classes = useStyles();

  const [isRequired, setIsRequired] = useState<boolean>(false);

  const handleChange = (value: unknown): InputValidationResult => {
    const validationResult = validateField(field, value);

    if (onValueChangeOrValidated) {
      onValueChangeOrValidated(field.id, value, validationResult.isValid);
    }

    return validationResult;
  };

  const getFieldLayout = (): JSX.Element => {
    // Stack number field is a speciel field and is matched manually for now
    if (field.id === stackNumberFieldId) {
      return (
        <StackNumberField
          field={field}
          required={isRequired}
          defaultValue={
            defaultValue !== undefined ? (defaultValue as number) : undefined
          }
          onChangeValidator={handleChange}
          disabled={disabled}
        />
      );
    }

    switch (field.fieldType) {
      case FieldTypeEnumResponse.INTEGER:
      case FieldTypeEnumResponse.DOUBLE: {
        return (
          <NumberField
            field={field}
            required={isRequired}
            value={
              defaultValue !== undefined ? (defaultValue as number) : undefined
            }
            onChangeValidator={handleChange}
            disabled={disabled}
          />
        );
      }
      case FieldTypeEnumResponse.IMAGE:
        return (
          <ImageField
            field={field}
            value={defaultValue as string}
            onChangeValidator={handleChange}
            disabled={disabled}
          />
        );
      case FieldTypeEnumResponse.OPTIONS:
        return (
          <OptionsField
            field={field}
            required={isRequired}
            value={defaultValue as string}
            onChangeValidator={handleChange}
            disabled={disabled}
          />
        );
      case FieldTypeEnumResponse.TEXT:
        return (
          <TextField
            type="text"
            multiline
            rows={5}
            field={field}
            required={isRequired}
            value={defaultValue ? (defaultValue as string) : undefined}
            onChangeValidator={handleChange}
            disabled={disabled}
          />
        );
      case FieldTypeEnumResponse.KEY_VALUE_PAIR_STRING_INT:
        return (
          <KeyValuePairStringIntField
            field={field as FieldWithKeyValuePairStringIntResponse}
            defaultValue={
              defaultValue
                ? (defaultValue as KeyValuePairStringIntFieldValue)
                : undefined
            }
            onChangeValidator={handleChange}
            disabled={disabled}
          />
        );
      case FieldTypeEnumResponse.BOOLEAN:
        return (
          <BooleanField
            field={field}
            required={isRequired}
            value={defaultValue as boolean}
            onChangeValidator={handleChange}
            disabled={disabled}
          />
        );
      case FieldTypeEnumResponse.STRING:
        return (
          <TextField
            type="text"
            field={field}
            required={isRequired}
            value={defaultValue ? (defaultValue as string) : undefined}
            onChangeValidator={handleChange}
            disabled={disabled}
          />
        );
      case FieldTypeEnumResponse.MULTIPLE_OPTIONS:
        return (
          <MultiOptionsField
            field={field}
            required={isRequired}
            value={defaultValue ? (defaultValue as Array<string>) : undefined}
            onChangeValidator={handleChange}
            disabled={disabled}
          />
        );
      case FieldTypeEnumResponse.DATE:
      case FieldTypeEnumResponse.POSITION:
      case FieldTypeEnumResponse.ARRAY_OF_INT:
      case FieldTypeEnumResponse.ARRAY_OF_DOUBLE:
      case FieldTypeEnumResponse.MANUAL_AREA:
      case FieldTypeEnumResponse.DEDUCTIONS:
      default:
        console.warn('Unsupported field type', field.fieldType);
        return <></>;
    }
  };

  useEffect(() => {
    const isRequired =
      field.validationRules?.some(
        (rule) =>
          rule.type === ValidationTypeEnumResponse.REQUIRED_FOR_PREREGISTRATION,
      ) ?? false;
    setIsRequired(isRequired);
  }, [field]);

  useEffect(() => {
    const validationResult = validateField(field, defaultValue);
    if (onValueChangeOrValidated) {
      onValueChangeOrValidated(
        field.id,
        defaultValue,
        validationResult.isValid,
      );
    }
  }, []);

  return <Box className={classes.container}>{getFieldLayout()}</Box>;
};

export { RegistrationFlowField };
